import type { HslAdjustment, PresetSettings } from "@lpa/preset-model";

export const RENDERER_VERSION = "cpu-linear-0.3.0";

export interface PixelBuffer {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

const clamp = (value: number, min = 0, max = 1) => Math.max(min, Math.min(max, value));
const srgbToLinear = (value: number) => value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
const linearToSrgb = (value: number) => value <= 0.0031308 ? value * 12.92 : 1.055 * value ** (1 / 2.4) - 0.055;

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const max = Math.max(r, g, b); const min = Math.min(r, g, b); const lightness = (max + min) / 2;
  if (max === min) return [0, 0, lightness];
  const delta = max - min; const saturation = lightness > .5 ? delta / (2 - max - min) : delta / (max + min);
  let hue = max === r ? (g - b) / delta + (g < b ? 6 : 0) : max === g ? (b - r) / delta + 2 : (r - g) / delta + 4;
  hue /= 6;
  return [hue, saturation, lightness];
}

function hueToRgb(p: number, q: number, hue: number): number {
  let h = hue; if (h < 0) h += 1; if (h > 1) h -= 1;
  if (h < 1 / 6) return p + (q - p) * 6 * h;
  if (h < 1 / 2) return q;
  if (h < 2 / 3) return p + (q - p) * (2 / 3 - h) * 6;
  return p;
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) return [l, l, l];
  const q = l < .5 ? l * (1 + s) : l + s - l * s; const p = 2 * l - q;
  return [hueToRgb(p, q, h + 1 / 3), hueToRgb(p, q, h), hueToRgb(p, q, h - 1 / 3)];
}

const hueCenters: Array<[string, number]> = [["red", 0], ["orange", 30], ["yellow", 60], ["green", 120], ["aqua", 180], ["blue", 240], ["purple", 275], ["magenta", 315]];
function hueDistance(a: number, b: number): number { const distance = Math.abs(a - b) % 360; return Math.min(distance, 360 - distance); }

function combinedHslAdjustment(hueDegrees: number, adjustments: Record<string, HslAdjustment>): HslAdjustment {
  let total = 0; let hue = 0; let saturation = 0; let luminance = 0;
  for (const [name, center] of hueCenters) {
    const weight = clamp(1 - hueDistance(hueDegrees, center) / 45);
    const adjustment = adjustments[name];
    if (!adjustment || weight === 0) continue;
    total += weight; hue += (adjustment.hue ?? 0) * weight; saturation += (adjustment.saturation ?? 0) * weight; luminance += (adjustment.luminance ?? 0) * weight;
  }
  return total ? { hue: hue / total, saturation: saturation / total, luminance: luminance / total } : {};
}

function toneChannel(value: number, luminance: number, settings: PresetSettings): number {
  const light = settings.light; let result = value * 2 ** (light.exposure ?? 0);
  const shadows = (light.shadows ?? 0) / 100; const highlights = (light.highlights ?? 0) / 100;
  const shadowMask = (1 - luminance) ** 2; const highlightMask = luminance ** 2;
  result += shadows * shadowMask * .35; result += highlights * highlightMask * .28;
  result += ((light.whites ?? 0) / 100) * luminance ** 4 * .25;
  result += ((light.blacks ?? 0) / 100) * (1 - luminance) ** 4 * .2;
  const contrast = 1 + (light.contrast ?? 0) / 100;
  return clamp((result - .18) * contrast + .18);
}

function applyLut(r: number, g: number, b: number, settings: PresetSettings): [number, number, number] {
  const lut = settings.lut; if (!lut) return [r, g, b];
  const normalized = [r, g, b].map((value, index) => clamp((value - lut.domainMin[index]!) / (lut.domainMax[index]! - lut.domainMin[index]!)));
  if (lut.dimension === 1) return normalized.map((value, channel) => { const position = value * (lut.size - 1); const low = Math.floor(position); const high = Math.min(lut.size - 1, low + 1); const mix = position - low; return clamp((lut.values[low * 3 + channel] ?? 0) * (1 - mix) + (lut.values[high * 3 + channel] ?? 0) * mix); }) as [number, number, number];
  const positions = normalized.map((value) => value * (lut.size - 1)); const lows = positions.map(Math.floor); const highs = lows.map((value) => Math.min(lut.size - 1, value + 1)); const mixes = positions.map((value, index) => value - lows[index]!);
  const sample = (ri: number, gi: number, bi: number, channel: number) => lut.values[((bi * lut.size + gi) * lut.size + ri) * 3 + channel] ?? 0;
  return [0, 1, 2].map((channel) => { let result = 0; for (let bz = 0; bz < 2; bz += 1) for (let gy = 0; gy < 2; gy += 1) for (let rx = 0; rx < 2; rx += 1) { const weight = (rx ? mixes[0]! : 1 - mixes[0]!) * (gy ? mixes[1]! : 1 - mixes[1]!) * (bz ? mixes[2]! : 1 - mixes[2]!); result += sample(rx ? highs[0]! : lows[0]!, gy ? highs[1]! : lows[1]!, bz ? highs[2]! : lows[2]!, channel) * weight; } return clamp(result); }) as [number, number, number];
}

export function renderPixels(input: PixelBuffer, settings: PresetSettings): PixelBuffer {
  const output = new Uint8ClampedArray(input.data); const temperature = settings.whiteBalance.temperature ?? 6500; const tint = settings.whiteBalance.tint ?? 0;
  const warmth = clamp((temperature - 6500) / 12000, -.38, .38); const tintShift = clamp(tint / 300, -.35, .35);
  const saturation = (settings.color.saturation ?? 0) / 100; const vibrance = (settings.color.vibrance ?? 0) / 100; const vignette = (settings.effects.vignette ?? 0) / 100;
  for (let index = 0; index < output.length; index += 4) {
    let r = srgbToLinear(output[index]! / 255); let g = srgbToLinear(output[index + 1]! / 255); let b = srgbToLinear(output[index + 2]! / 255);
    const luminance = .2126 * r + .7152 * g + .0722 * b;
    r = toneChannel(r, luminance, settings); g = toneChannel(g, luminance, settings); b = toneChannel(b, luminance, settings);
    r = clamp(r * (1 + warmth * .35 + tintShift * .08)); g = clamp(g * (1 - tintShift * .14)); b = clamp(b * (1 - warmth * .35 + tintShift * .08));
    let sr = clamp(linearToSrgb(r)); let sg = clamp(linearToSrgb(g)); let sb = clamp(linearToSrgb(b));
    let [h, s, l] = rgbToHsl(sr, sg, sb); const hsl = combinedHslAdjustment(h * 360, settings.hsl);
    h = (h + (hsl.hue ?? 0) / 600 + 1) % 1;
    const adaptiveVibrance = vibrance * (1 - s) * .8;
    s = clamp(s * (1 + saturation + adaptiveVibrance) + (hsl.saturation ?? 0) / 100);
    l = clamp(l + (hsl.luminance ?? 0) / 200);
    [sr, sg, sb] = hslToRgb(h, s, l);
    [sr, sg, sb] = applyLut(sr, sg, sb, settings);
    if (vignette !== 0) {
      const pixel = index / 4; const x = pixel % input.width; const y = Math.floor(pixel / input.width);
      const dx = (x / Math.max(1, input.width - 1) - .5) * 2; const dy = (y / Math.max(1, input.height - 1) - .5) * 2;
      const edge = clamp((Math.sqrt(dx * dx + dy * dy) - .35) / .75); const factor = 1 + vignette * edge * .45;
      sr = clamp(sr * factor); sg = clamp(sg * factor); sb = clamp(sb * factor);
    }
    output[index] = Math.round(sr * 255); output[index + 1] = Math.round(sg * 255); output[index + 2] = Math.round(sb * 255);
  }
  return { data: output, width: input.width, height: input.height };
}
